"use client";

import { motion } from "motion/react";
import Image from "next/image";
import {
  Award,
  Users,
  Heart,
  Target,
  Shield,
  Globe,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";
import { useAppSelector } from "@/_hooks/hooks";
import { ICMS } from "@/store/homeSlice";
import { useEffect, useState } from "react";
import { createLookupObject, cleanHeaderTags } from "@/_helper/helper";

export default function AboutUsClient({ cmsData }: { cmsData: ICMS[] }) {
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});
  const banner: any =
    BannerList?.find((banner: any) => banner?.BannerType === "about") || {};

  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  // const stats = [
  //   { icon: Users, value: "50,000+", label: "Happy Travelers" },
  //   { icon: Award, value: "15+", label: "Years Experience" },
  //   { icon: Globe, value: "100+", label: "Destinations" },
  //   { icon: TrendingUp, value: "98%", label: "Customer Satisfaction" },
  // ];

  const values = [
    {
      icon: Heart,
      title: "Customer First",
      description:
        "Your satisfaction and safety are our top priorities. We go the extra mile to ensure every journey is memorable.",
      color: "from-red-500 to-pink-600",
    },
    {
      icon: Shield,
      title: "Trust & Reliability",
      description:
        "With transparent pricing and honest service, we have built trust with thousands of satisfied customers.",
      color: "from-blue-500 to-indigo-600",
    },
    {
      icon: Target,
      title: "Excellence",
      description:
        "We strive for perfection in every detail, from booking to the final goodbye, ensuring a seamless experience.",
      color: "from-orange-500 to-amber-600",
    },
  ];

  const team = [
    {
      name: "Rahul Sharma",
      role: "Founder & CEO",
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=400",
      bio: "15 years of experience in tourism industry",
    },
    {
      name: "Priya Patel",
      role: "Head of Operations",
      image:
        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=400",
      bio: "Expert in hospitality management",
    },
    {
      name: "Amit Kumar",
      role: "Tour Manager",
      image:
        "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=400",
      bio: "Certified tour guide with 10+ years",
    },
  ];

  const milestones = [
    {
      year: "2010",
      title: "Company Founded",
      description: "Started with a vision to revolutionize Gujarat tourism",
    },
    {
      year: "2015",
      title: "Award Winning Service",
      description: "Received Best Tourism Service Provider award",
    },
    {
      year: "2018",
      title: "Expanded Operations",
      description: "Opened offices in 5 major cities across Gujarat",
    },
    {
      year: "2023",
      title: "Digital Transformation",
      description: "Launched AI-powered booking platform",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[50vh] overflow-hidden">
        {banner?.BannerImage && (
          <Image
            src={banner?.BannerImage || ""}
            alt={banner?.BannerTitle || "Banner Image"}
            fill
            className="object-cover"
            priority
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-gray-900/0 to-gray-900/0 pointer-events-none"></div>

        <div className="absolute inset-0 flex items-end pointer-events-none">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold text-amber-300 mb-6 drop-shadow-2xl">
                {banner?.BannerTitle || "Premium Hotels"}
              </h1>
              {banner?.Description && (
                <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto drop-shadow-lg mb-6">
                  {banner?.Description}
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-b from-white to-gray-50">
        {lookUpCMSData?.["About - Section 1: Stats"] && (
          <div
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 blog-content"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(lookUpCMSData?.["About - Section 1: Stats"]?.Template),
            }}
          />
        )}
        {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl mb-4 shadow-lg">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div> */}
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {lookUpCMSData?.["About - Section 2: Our story"] && (
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="blog-content"
                dangerouslySetInnerHTML={{
                  __html:
                    cleanHeaderTags(lookUpCMSData?.["About - Section 2: Our story"]?.Template),
                }}
              />
            )}
            {/* <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Our{" "}
                <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                  Story
                </span>
              </h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  Founded in 2010, Aalpine has been at the forefront of
                  Gujarat's tourism industry, specializing in creating memorable
                  experiences at the iconic Statue of Unity and beyond.
                </p>
                <p>
                  What started as a small travel agency in Ahmedabad has grown
                  into Gujarat's most trusted tourism partner, serving over
                  50,000 satisfied travelers from across the globe.
                </p>
                <p>
                  Our passion for showcasing India's rich heritage, combined
                  with modern hospitality standards, has made us the preferred
                  choice for families, couples, and solo travelers seeking
                  authentic experiences.
                </p>
                <p>
                  Today, we continue to innovate and set new standards in
                  tourism, offering curated packages, premium accommodations,
                  and personalized services that exceed expectations.
                </p>
              </div>
            </motion.div> */}

            {lookUpCMSData?.["About - Section 2: Our story"] && (
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl h-96">
                  <Image
                    src={
                      lookUpCMSData?.["About - Section 2: Our story"]
                        .CMSImage ||
                      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800"
                    }
                    // src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800"
                    alt={
                      lookUpCMSData?.["About - Section 2: Our story"]
                        .CMSImageAlterTag || "Our Story"
                    }
                    // alt="Our Team"
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl -z-10 pointer-events-none"></div>
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-orange-300 to-amber-400 rounded-2xl -z-10 pointer-events-none"></div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Our Values */}
      {lookUpCMSData?.["About - Section 3: Our Values"] && (
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 blog-content"
            dangerouslySetInnerHTML={{
              __html:
                cleanHeaderTags(lookUpCMSData?.["About - Section 3: Our Values"]?.Template),
            }}
          ></div>
        </section>
      )}
      {/* <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our{" "}
              <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                Values
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300"
              >
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${value.color} rounded-2xl mb-6 shadow-lg`}
                >
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Timeline */}
      {lookUpCMSData?.["About - Section 4: Our Journey"] && (
        <section className="py-16 bg-white">
          <div
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 blog-content"
            dangerouslySetInnerHTML={{
              __html:
                cleanHeaderTags(lookUpCMSData?.["About - Section 4: Our Journey"]?.Template),
            }}
          ></div>
        </section>
      )}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our{" "}
              <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                Journey
              </span>
            </h2>
            <p className="text-xl text-gray-600">
              Key milestones that shaped our success
            </p>
          </motion.div>

          <div className="relative border-l border-gray-200 md:border-l-0">
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-orange-500 to-amber-600"></div>

            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative flex flex-col md:flex-row items-center mb-12 pl-6 md:pl-0 ${
                  index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
              >
                <div
                  className={`w-full md:w-5/12 ${
                    index % 2 === 0
                      ? "md:text-right md:pr-12"
                      : "md:text-left md:pl-12"
                  }`}
                >
                  <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                    <div className="inline-block bg-gradient-to-r from-orange-500 to-amber-600 text-white px-4 py-2 rounded-full font-bold text-sm mb-3">
                      {milestone.year}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {milestone.title}
                    </h3>
                    <p className="text-gray-600">{milestone.description}</p>
                  </div>
                </div>

                <div className="absolute left-[-11px] md:left-1/2 md:transform md:-translate-x-1/2 w-6 h-6 bg-gradient-to-br from-orange-500 to-amber-600 rounded-full border-4 border-white shadow-lg md:block top-6 md:top-auto"></div>

                <div className="w-full md:w-5/12 hidden md:block"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Team Section */}
      {/* {lookUpCMSData?.["About - Section 5: Meet Our Team"] && (
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
            dangerouslySetInnerHTML={{
              __html:
                cleanHeaderTags(lookUpCMSData?.["About - Section 5: Meet Our Team"]?.Template),
            }}
          ></div>
        </section>
      )} */}
      {/* <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Meet Our{" "}
              <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                Team
              </span>
            </h2>
            <p className="text-xl text-gray-600">
              Passionate professionals dedicated to your journey
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300">
                  <div className="relative overflow-hidden aspect-square">
                    <Image
                      src={member.image}
                      alt={member.name}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                      {member.name}
                    </h3>
                    <p className="text-orange-600 font-semibold mb-3">
                      {member.role}
                    </p>
                    <p className="text-gray-600">{member.bio}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Why Choose Us */}
      {lookUpCMSData?.["About - Section 6: Why Choose"] && (
        <section className="py-16 bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 text-white">
          <div
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 blog-content"
            dangerouslySetInnerHTML={{
              __html:
                cleanHeaderTags(lookUpCMSData?.["About - Section 6: Why Choose"]?.Template),
            }}
          ></div>
        </section>
      )}
      {/* <section className="py-16 bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Choose Aalpine?
            </h2>
            <p className="text-xl text-white/90">
              Experience the difference with our premium services
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              "Best Price Guarantee",
              "Instant Confirmation",
              "24/7 Customer Support",
              "Expert Local Guides",
              "Flexible Cancellation",
              "Customized Packages",
              "Safe & Secure Booking",
              "Verified Accommodations",
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
              >
                <CheckCircle2 className="w-6 h-6 text-white flex-shrink-0" />
                <span className="font-semibold">{feature}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}
    </div>
  );
}
